import {FormControl, FormField, Option} from 'platform/components';
import {GridItem, Grid, Space, VStack, Show} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';

import {suffixTestId, RequiredTestIdProps, Nullish} from 'shared/web';

import {useWarehouseAvailableTemplates} from '../../../../hooks/useWarehouseAvailableTemplates';
import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../types/WarehouseDirectSaleVariantsDetailFormSchema';

interface DocumentsTabProps extends RequiredTestIdProps {
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  onDiscard: VoidFunction;
  directSaleVariantId: string | Nullish;
  offerDocSeriesOptions: Option[];
  fastTrackDocSeriesOptions: Option[];
}

export function DocumentsTab(props: DocumentsTabProps) {
  const {getOptions: getTemplateOptions, isLoading: isTemplatesLoading} =
    useWarehouseAvailableTemplates({directSaleVariantId: props.directSaleVariantId ?? undefined});

  const isFastTrackEnabled = useWatch({
    control: props.control,
    name: 'documents.fastTrackIssue.isEnable',
  });

  const offerTemplateOptions = getTemplateOptions('direct-sale-offer-html');
  const fastTrackTemplateOptions = getTemplateOptions('workshop-fast_track');

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <Grid columns={2}>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.docSeriesId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.offerSeries')}
                options={props.offerDocSeriesOptions}
                data-testid={suffixTestId('offerSeriesId', props)}
              />
            </GridItem>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.templateId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.offerTemplate')}
                options={offerTemplateOptions}
                isLoading={isTemplatesLoading}
                data-testid={suffixTestId('offerTemplateId', props)}
              />
            </GridItem>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.fastTrackIssue.isEnable"
                type="switch"
                label={i18n.t('entity.warehouse.labels.enableFastTrackIssue')}
                data-testid={suffixTestId('isFastTrackEnabled', props)}
              />
            </GridItem>
            <GridItem span={1} />
            <Show when={isFastTrackEnabled}>
              <GridItem span={1}>
                <FormField
                  isRequired
                  control={props.control}
                  name="documents.fastTrackIssue.docSeriesId"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.fastTrackSeries')}
                  options={props.fastTrackDocSeriesOptions}
                  data-testid={suffixTestId('fastTrackDocSeriesId', props)}
                />
              </GridItem>
              <GridItem span={1}>
                <FormField
                  control={props.control}
                  name="documents.fastTrackIssue.templateId"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.fastTrackTemplate')}
                  options={fastTrackTemplateOptions}
                  isLoading={isTemplatesLoading}
                  data-testid={suffixTestId('fastTrackTemplateId', props)}
                />
              </GridItem>
            </Show>
          </Grid>
        </VStack>
      </SettingsSection>

      <Space vertical={18} />

      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.discard')
              : i18n.t('general.actions.discardChanges'),
            onClick: props.onDiscard,
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.save')
              : i18n.t('general.actions.saveChanges'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
      />
    </>
  );
}
